import { Dispatch, FunctionComponent, RefObject, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

import { CollapseGrabber } from "@/components/CollapseGrabber/CollapseGrabber";
import { Spinner } from "@/components/Spinner/Spinner";
import { classNames } from "@/utils/classNames";
import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { useAppSelector } from "@/redux/hooks";

import { ListPerson } from "../../../List/schema/list";
import { OutboxLeadCompact } from "../../components/LeadCardCompact/OutboxLeadCompact";
import { MessagesEmptyColumn } from "../../components/MessagesEmptyColumn/MessagesEmptyColumn";
import { MessagesLoadError } from "../../components/MessagesLoadError/MessagesLoadError";
import { ListScope } from "../../components/ListScopeFilter/listScope";
import { getCurrentCategory } from "./outbox-category.helper";
import { getAllTabCategory, getDividerContent } from "./outbox-divider.helper";
import { OutboxStatusTab } from "./outboxStatusTabs";

import type { CustomListsAutocompleteForSequencerItem } from "../../../List/components/CustomListsAutocompleteForSequencer";

interface OutboxLeadsColumnProps {
	rows: ListPerson[];
	showLeads: boolean;
	onToggleShowLeads: () => void;
	selected: number[];
	setSelected: Dispatch<SetStateAction<number[]>>;
	handleSetCurrentLead: (index: number) => void;
	isFetching: boolean;
	/** Whether another page can still be loaded (drives the sentinel spinner). */
	hasMorePages: boolean;
	/** Sentinel observed by the infinite-scroll effect in the page component. */
	leadsEndRef: RefObject<HTMLDivElement>;
	todayStart: Date;
	/** Active status tab — the All tab groups by last-sent day instead of category. */
	statusTab: OutboxStatusTab;
	/** The applied list filter — carried over to the Inbox by the empty state's cross-link. */
	selectedLists: CustomListsAutocompleteForSequencerItem[];
	/** Whether the user is searching — picks the empty-state copy. */
	hasSearchQuery: boolean;
}

/**
 * The collapsible Outbox leads column: category dividers (scheduled/sent/paused
 * buckets from `getCurrentCategory`), the lead cards, and the infinite-scroll
 * sentinel. Mirrors `InboxLeadsColumn`.
 */
export const OutboxLeadsColumn: FunctionComponent<OutboxLeadsColumnProps> = ({
	rows,
	showLeads,
	onToggleShowLeads,
	selected,
	setSelected,
	handleSetCurrentLead,
	isFetching,
	hasMorePages,
	leadsEndRef,
	todayStart,
	statusTab,
	selectedLists,
	hasSearchQuery,
}) => {
	const { t } = useTranslation();
	const { inboxLoadFailed } = useAppSelector(listSelector);

	const currentCategoryOf = (listPerson: ListPerson) =>
		statusTab === OutboxStatusTab.ALL
			? getAllTabCategory(listPerson)
			: getCurrentCategory(listPerson, todayStart);

	return (
		<div
			className={`bg-white w-[380px] min-w-[380px] transition-all z-15 duration-300 h-full border-r pl-0 border-border-light pb-0 relative ${
				showLeads ? "translate-x-0" : "translate-x-[0] !w-[35px] !min-w-[35px]"
			}`}
		>
			<CollapseGrabber
				ariaLabel={showLeads ? t("basics.collapse") : t("basics.expand")}
				collapsed={!showLeads}
				edge="right"
				onToggle={onToggleShowLeads}
			/>
			<div className={classNames("overflow-x-hidden h-full flex flex-col")}>
				<div
					className="flex flex-col max-h-full overflow-y-auto border-t border-border-light scrollbar-hide w-full"
					id="leads-container"
				>
					{rows.map((row: ListPerson, index: number) => {
						const prevRow = index > 0 ? rows[index - 1] : null;

						const currentCategory = currentCategoryOf(row);
						const previousCategory = prevRow ? currentCategoryOf(prevRow) : null;

						const showDivider =
							currentCategory && (index === 0 || currentCategory !== previousCategory);

						const dividerContent = currentCategory ? getDividerContent(currentCategory, t) : null;

						return (
							<div key={`${row.id}-${row.prospect?.name}`}>
								{showDivider && showLeads && dividerContent && (
									<div
										className={`w-full ${dividerContent.bgClass} ${dividerContent.textClass} text-sm p-2 font-bold border border-border-light justify-center items-center text-left`}
									>
										{dividerContent.text}
									</div>
								)}

								<OutboxLeadCompact
									key={`${row.id}-${row.prospect?.name}`}
									handleSetCurrentLead={handleSetCurrentLead}
									isCollapsed={!showLeads}
									row={row}
									selected={selected}
									setSelected={setSelected}
									todayStart={todayStart}
								/>
							</div>
						);
					})}
					{showLeads && !isFetching && !inboxLoadFailed && rows.length === 0 && (
						<MessagesEmptyColumn
							hasSearchQuery={hasSearchQuery}
							scope={ListScope.OUTBOX}
							selectedLists={selectedLists}
						/>
					)}

					{showLeads && <MessagesLoadError />}

					{!isFetching && !inboxLoadFailed && hasMorePages ? (
						<div ref={leadsEndRef} className="flex items-center py-2 justify-center">
							<Spinner color="#4d66f5" />
						</div>
					) : isFetching ? (
						<div className="flex items-center justify-center py-2">
							<Spinner color="#4d66f5" />
						</div>
					) : (
						<></>
					)}
				</div>
			</div>
		</div>
	);
};
